import { type BoardCard, type BoardColumn, contentPath, ideaBatchPath } from '../api.ts';
import { attentionReason } from '../data.tsx';

/**
 * The board's shape in the browser: its seven columns, the two kinds of card,
 * and the order inside a column (Design reference § Columns, § Order inside a
 * column). The server decides which column a card stands in; the order is
 * decided here, because "needs you" is decided here.
 */

/** The columns from left to right; the server's `BOARD_COLUMNS`. */
export const BOARD_COLUMNS: readonly { readonly id: BoardColumn; readonly label: string }[] = [
  { id: 'ideas', label: 'Ideas' },
  { id: 'planning', label: 'Planning' },
  { id: 'production', label: 'Production' },
  { id: 'review', label: 'Review' },
  { id: 'to-post', label: 'To post' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'posted', label: 'Posted' },
];

export type { BoardCard, BoardColumn };
export type IdeaCard = Extract<BoardCard, { type: 'idea' }>;
export type ContentCard = Extract<BoardCard, { type: 'content' }>;

/**
 * Whether a card waits on the operator: an idea to decide or that could not be
 * worked out, or a content `attentionReason` has a reason for.
 */
export function needsYou(card: BoardCard): boolean {
  if (card.type === 'idea') return card.idea.status === 'drafted' || card.idea.status === 'failed';
  return attentionReason(card.content) !== null;
}

/** Where a card's title leads: the content page, or the idea in its batch's review. */
export function cardHref(card: BoardCard): string {
  return card.type === 'content'
    ? contentPath(card.content.id)
    : `${ideaBatchPath(card.batch.id)}/review?idea=${encodeURIComponent(card.idea.id)}`;
}

/** The title a card shows: the content's name, or the idea's best name for itself. */
export function cardTitle(card: BoardCard): string {
  if (card.type === 'content') return card.content.name;
  return card.idea.title ?? card.idea.sourceTitle ?? card.idea.seed;
}

/** Every card in its column, each column in its own order. */
export function groupByColumn(cards: readonly BoardCard[]): Record<BoardColumn, BoardCard[]> {
  const grouped = Object.fromEntries(BOARD_COLUMNS.map(({ id }) => [id, [] as BoardCard[]])) as Record<
    BoardColumn,
    BoardCard[]
  >;
  for (const card of cards) grouped[card.column].push(card);
  for (const { id } of BOARD_COLUMNS) grouped[id] = sortColumn(id, grouped[id]);
  return grouped;
}

type Key = number | string | null;

/**
 * Compares on each key in turn; a `null` key sorts after every value. Strings
 * here are ISO-8601 instants, which order as text.
 */
function compareKeys(a: readonly Key[], b: readonly Key[]): number {
  for (let index = 0; index < a.length; index += 1) {
    const left = a[index] ?? null;
    const right = b[index] ?? null;
    if (left === right) continue;
    if (left === null) return 1;
    if (right === null) return -1;
    return left < right ? -1 : 1;
  }
  return 0;
}

function boardRank(card: BoardCard): number {
  return card.type === 'idea' ? card.idea.boardRank : card.content.boardRank;
}

function createdAt(card: BoardCard): string {
  return card.type === 'idea' ? card.idea.createdAt : card.content.createdAt;
}

/** Production's four groups, in the order the column shows them. */
function productionGroup(card: ContentCard): number {
  const { content } = card;
  if (needsYou(card)) return 0;
  if (content.status === 'producing' || content.status === 'waiting') return 1;
  if (content.queuePosition !== null) return 2;
  if (content.scheduledStartAt !== null) return 3;
  return 4;
}

/** What a column is sorted on, per card; earlier keys win. */
function sortKeys(column: BoardColumn, card: BoardCard): Key[] {
  if (card.type === 'idea') return [card.idea.boardRank, card.idea.createdAt];
  const { content } = card;
  switch (column) {
    case 'production': {
      const group = productionGroup(card);
      if (group === 2) return [group, content.queuePosition];
      if (group === 3) return [group, content.scheduledStartAt];
      return [group, content.updatedAt];
    }
    case 'review':
      return [needsYou(card) ? 0 : 1, content.updatedAt];
    case 'scheduled':
      return [content.posts.next?.scheduledAt ?? null];
    case 'posted':
      // Latest first: the negated time, so `null` still lands last.
      return [content.placedAt === null ? null : -Date.parse(content.placedAt)];
    default:
      return [boardRank(card), createdAt(card)];
  }
}

/**
 * One column's cards in the order Design reference § Order inside a column
 * gives it. Ties keep the server's order.
 */
export function sortColumn(column: BoardColumn, cards: readonly BoardCard[]): BoardCard[] {
  return cards
    .map((card, index) => ({ card, index, keys: sortKeys(column, card) }))
    .sort((a, b) => compareKeys(a.keys, b.keys) || a.index - b.index)
    .map(({ card }) => card);
}
